/**
 * Layout Engine — pure math module.
 *
 * Phase 3: Mock State → Computed State (angles, positions, bounds).
 *
 * VC-011 Stage 2: ring layout delegates to the Ring Layout Authority
 * (Layer 2, src/engine/renderer/ringLayout.js) fed by font-file metrics
 * (Layer 1, src/engine/renderer/fontMetrics.js). Canvas measureText is
 * gone — the browser no longer decides where glyphs go. Center layout
 * below is unchanged (center text is Stage 3).
 *
 * No SVG rendering. No DOM references. Reads Typography Config + Content
 * + Scale. Outputs geometry only.
 */

// ── Sticker geometry constants ──────────────────────────────
// Single source of truth (R-1, 2026-08-07): previously duplicated as
// svgBuilder.js's CX/CY and this file's literal 600/600. Classic scripts
// share the global lexical scope, so svgBuilder.js reads these; the
// renderer must NOT re-declare its own.
const CENTER_X = 600;
const CENTER_Y = 600;

// ── Ring Layout ───────────────────────────────────────────

/**
 * Layer 1+2 guard (WO §7, Advisor ruling Q2): the font modules are ES
 * modules; classic call sites must throw when they are absent — never
 * continue with undefined.
 */
function ringModules() {
  const eng = window.StickerEngine || {};
  if (!eng.fontMetrics || !eng.ringLayout) {
    throw new Error('StickerEngine font modules not loaded (fontMetrics.js + ringLayout.js must load before layout)');
  }
  return eng;
}

/**
 * Compute layout for one ring.
 *
 * @param {object} ring — { topText, bottomText, fontFamily, fontWeight, topFontSize, bottomFontSize, letterSpacing, ringScale, trackRadius }
 * @param {boolean} hasDecorations — true for outer ring, false for inner
 * @returns {object} ring computed state — { layout, topTextArc, bottomTextArc, decorations }
 */
function computeRingLayout(ring, hasDecorations) {
  const eng = ringModules();
  const scaledRadius = ring.trackRadius * (ring.ringScale || 1.0);

  const input = {
    centerX: CENTER_X, centerY: CENTER_Y,
    offsetY: ring.offsetY || 0,
    trackRadius: ring.trackRadius || 0,
    ringScale: ring.ringScale || 1.0,
    rotation: ring.rotation || 0,
    top: null,
    bottom: null,
  };

  if (ring.topText && ring.topText.trim().length > 0) {
    const font = eng.fontMetrics.getFont(ring.fontFamily, ring.fontWeight);
    input.top = {
      chars: eng.fontMetrics.charMetrics(font, ring.topText, ring.topFontSize),
      capHeight: eng.fontMetrics.capHeight(font, ring.topFontSize),
      letterSpacing: ring.letterSpacing || 0,
    };
  }

  if (ring.bottomText && ring.bottomText.trim().length > 0) {
    const font = eng.fontMetrics.getFont(ring.fontFamily, ring.fontWeight);
    input.bottom = {
      chars: eng.fontMetrics.charMetrics(font, ring.bottomText, ring.bottomFontSize),
      capHeight: eng.fontMetrics.capHeight(font, ring.bottomFontSize),
      letterSpacing: ring.letterSpacing || 0,
    };
  }

  const layout = eng.ringLayout.computeRingLayout(input);

  return {
    offsetY: ring.offsetY || 0,
    trackRadius: scaledRadius,
    // Layer 2 output is the single authority: per-char anchors/rotations,
    // spans, and decoration dots (absolute coords, offsetY + rotation
    // already applied). topTextArc/bottomTextArc/decorations below are
    // derived views kept for compatibility.
    layout: layout,
    topTextArc: layout.top ? {
      startAngle: layout.top.startAngle,
      endAngle: layout.top.endAngle,
      span: layout.top.span,
    } : null,
    bottomTextArc: layout.bottom ? {
      startAngle: layout.bottom.startAngle,
      endAngle: layout.bottom.endAngle,
      span: layout.bottom.span,
    } : null,
    decorations: layout.decorations.map(d => ({ side: d.side, angle: d.angle, x: d.x, y: d.y })),
  };
}

// ── Center Layout ─────────────────────────────────────────

/**
 * Compute center area bounds.
 *
 * @param {object} center — { mode, centerScale }
 * @param {number} innerTrackRadius — scaled inner track radius
 * @returns {object} { width, height }
 */
function computeCenterBounds(center, innerTrackRadius) {
  // Center area sits inside the inner ring. Use 85% of inner track diameter
  // as the available square area, scaled by centerScale.
  const baseSize = innerTrackRadius * 2 * 0.85;
  const scale = center.centerScale || 1.0;
  const size = baseSize * scale;
  return { width: size, height: size };
}

/**
 * Compute bounding geometries for each center layer.
 *
 * VC-011 Stage 3 (S3-A/S3-D): center text layout is now real —
 * per-line width = Σ(advance + kerning) + letterSpacing × (chars − 1)
 * (Stage 1 ring rule, WO stage3 §4.1), baseline = CY + capHeight/2
 * (capHeight mid-split, §4.2), line height = fontSize × lineSpacing
 * (§4.3). The dead `longestLine.length * fs * 0.6` estimate and its
 * text1Bounds/text2Bounds/text3Bounds outputs are GONE (they were
 * never consumed by the render chain — S3-D).
 *
 * @param {object} center — center state block
 * @param {number} innerTrackRadius — scaled inner track radius
 * @param {object} fm — fontMetrics module (Layer 1)
 * @returns {object} { graphicBounds, centerText: { text1, text2, text3 } }
 */
function computeCenterLayout(center, innerTrackRadius, fm) {
  const baseSize = innerTrackRadius * 2 * 0.85;
  const globalScale = center.centerScale || 1.0;
  const base = baseSize * globalScale;

  const graphicScale = (center.graphic && center.graphic.scale) || 1.0;
  const graphicBounds = { width: base * graphicScale, height: base * graphicScale };

  // §4.1-4.3: center text layout authority (same rule family as the
  // Stage 1 ring layout — gap between glyphs = kerning + letterSpacing,
  // no trailing gap; single authority consumed by both the renderer and
  // the §5.2 geometry assertions via the layout dump).
  function layoutSlot(slot) {
    // Same visibility contract as the renderer (svgBuilder design path):
    // enabled !== false && visible && non-empty text — a slot the renderer
    // skips must not produce layout (dump/assert parity).
    if (!slot || slot.enabled === false || !slot.visible || !slot.text || !slot.text.trim()) return null;
    const font = fm.getFont(slot.fontFamily, slot.fontWeight);
    const fs = slot.fontSize || 120;
    const ls = slot.letterSpacing || 0;
    const lh = fs * (slot.lineSpacing || 1.2);
    const cap = fm.capHeight(font, fs);
    const lines = String(slot.text).split('\n').map(function (line) {
      const metrics = fm.charMetrics(font, line, fs);
      let width = 0;
      const chars = metrics.map(function (m, i) {
        const x = width;                       // local x from the line start
        const gap = i < metrics.length - 1 ? (m.kerning || 0) + ls : 0;
        width += m.advance + gap;
        return { char: m.char, advance: m.advance, kerning: m.kerning || 0, x: x };
      });
      return {
        text: line,
        width: width,                          // Σ advance+kerning + ls×(n−1)
        chars: chars,
      };
    });
    // §4.1 + Amendment 1 (R1, 2026-08-09): alignment parameter. Default
    // center keeps the ruled formula byte-identical; left/right use the
    // paragraph model — the slot's WIDEST line is the reference edge.
    const align = slot.textAlign || 'center';
    const maxW = lines.reduce(function (m, l) { return Math.max(m, l.width); }, 0);
    lines.forEach(function (l) {
      if (align === 'left') { l.startX = CENTER_X - maxW / 2; }
      else if (align === 'right') { l.startX = CENTER_X + maxW / 2 - l.width; }
      else { l.startX = CENTER_X - l.width / 2; }   // §4.1: line start, centred
    });
    const L = lines.length;
    const firstBaseline = CENTER_Y + cap / 2 - (L - 1) * lh / 2;  // §4.3 n=0
    lines.forEach(function (line, n) {
      line.baselineY = firstBaseline + n * lh;
    });
    return {
      fontSize: fs,
      capHeight: cap,
      lineHeight: lh,
      letterSpacing: ls,
      lines: lines,
    };
  }

  return {
    graphicBounds: graphicBounds,
    centerText: {
      text1: layoutSlot(center.text1),
      text2: layoutSlot(center.text2),
      text3: layoutSlot(center.text3),
    },
  };
}

// ── Main Layout Entry Point ───────────────────────────────

/**
 * Compute full sticker layout from Mock State.
 *
 * @param {object} state — Mock State with ring/center/decoration config
 * @returns {object} Computed State
 */
function computeLayout(state) {
  const outerRing = state.outerRing || {};
  const innerRing = state.innerRing || {};
  const design = state.sources && state.sources.design || {};
  const decoration = state.decoration || {};

  // Outer ring
  const outerResult = computeRingLayout(outerRing, true);

  // Inner ring
  const innerResult = computeRingLayout(innerRing, false);

  // Center bounds — reads from sources.design (center scale defaults to 1.0 in v0.42)
  const centerBounds = computeCenterBounds(design, innerResult.trackRadius || 240);
  // Stage 3: center text layout needs Layer 1 (fontMetrics) — same guard
  // as the ring path (ringModules throws when modules are absent).
  const centerLayout = computeCenterLayout(design, innerResult.trackRadius || 240, ringModules().fontMetrics);

  return {
    outerTrackRadius: outerResult.trackRadius,
    innerTrackRadius: innerResult.trackRadius,
    outerRing: {
      offsetY: outerResult.offsetY,
      layout: outerResult.layout,          // Layer 2 output — single authority
      topTextArc: outerResult.topTextArc,
      bottomTextArc: outerResult.bottomTextArc,
      decorations: outerResult.decorations,
    },
    innerRing: {
      offsetY: innerResult.offsetY,
      layout: innerResult.layout,          // Layer 2 output — single authority
      topTextArc: innerResult.topTextArc,
      bottomTextArc: innerResult.bottomTextArc,
      decorations: innerResult.decorations,
    },
    centerBounds: centerBounds,
    centerLayout: centerLayout,
    decoration: {
      baseSize: decoration.baseSize || 60,
    },
  };
}
